<script lang="ts">
  import { formatContactString } from "@retailer/services/SalesRepService";
  import {
    copyOrderToClipboard,
    downloadPdf,
    buildMailtoLink,
    formatSalesRepEmails,
  } from "@retailer/svelte/Shared/OrderHelpers";
  import LayoutStore from "@shared/svelte/Stores/LayoutStore";
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import { createEventDispatcher, onMount } from "svelte";
  import { EventTracking } from "@shared/services/event_tracking/events";
  import { cloneDeep } from "lodash";
  import type { RetailerOrder } from "@shared/types/Order";
  import layoutStore from "@shared/svelte/Stores/LayoutStore";
  import type { RetailerType } from "@shared/types/Retailer";
  import {
    DistributorEntity,
    OrderEntity,
    ManualOrderActionEntity,
  } from "@shared/services/event_tracking/entities";
  import ActionRequiredDetailProduct from "@retailer/svelte/Order/Components/ActionRequiredDetailProduct.svelte";

  export let order: RetailerOrder;
  export let retailer: RetailerType;
  const salesRepAddresses = formatSalesRepEmails(order);
  const { trackEvent, trackClick } = EventTracking;

  const eventDispatcher = createEventDispatcher();

  const updateOrderAsSent = (markAsSent = true) => {
    if (markAsSent && !order.manual_order_action_required) return;

    const updatedOrder = cloneDeep(order);
    updatedOrder.manual_order_action_required = markAsSent
      ? false
      : !updatedOrder.manual_order_action_required;

    if (!markAsSent)
      trackClick(null, [
        new OrderEntity({ id: order.id }),
        new DistributorEntity({ id: order.distributor.id }),
        new ManualOrderActionEntity({
          action_type: "clicks_mark_as_sent",
          action_result: updatedOrder.manual_order_action_required,
        }),
      ]);
    eventDispatcher("orderUpdated", { order: updatedOrder });
  };

  onMount(() => {
    trackEvent("View page", [new OrderEntity({ id: order.id })]);
  });
</script>

<style>
  .button-margin {
    margin-right: 0 !important;
  }
</style>

<div class="fizz-card fizz-bordered fizz-stack-24">
  <div class="fizz-card">
    <div class="fizz-space-between fizz-flex-row-responsive fizz-card-padded fizz-border-bottom">
      <div>
        <h4 class="fizz-heading-4">{order.distributor.name}</h4>
        <p class="fizz-text-subdued">
          {#each order.sales_reps as rep, i}
            {formatContactString(rep, i < order.sales_reps.length - 1)}
          {/each}
        </p>
      </div>
      <div class="fizz-flex-row-8 fizz-flex-no-shrink fizz-flex-wrap" style="--gap: 8px">
        {#if $LayoutStore.isDesktop}
          <button
            class="fizz-button fizz-button-small fizz-button-icon button-margin"
            on:click={() => downloadPdf(order)}
            on:click={() => updateOrderAsSent()}
            on:click={() =>
              trackClick(null, [
                new OrderEntity({ id: order.id }),
                new DistributorEntity({ id: order.distributor.id }),
                new ManualOrderActionEntity({
                  action_type: "clicks_download_pdf",
                  action_result: false,
                }),
              ])}
          >
            <Icon type="Save" />
            <span>Download PDF</span>
          </button>
        {/if}
        <button
          class="fizz-button fizz-button-small fizz-button-icon button-margin"
          on:click={() => copyOrderToClipboard(order)}
          on:click={() => updateOrderAsSent()}
          on:click={() =>
            trackClick(null, [
              new OrderEntity({ id: order.id }),
              new DistributorEntity({ id: order.distributor.id }),
              new ManualOrderActionEntity({
                action_type: "clicks_copy_order",
                action_result: false,
              }),
            ])}
        >
          <Icon type="Copy" />
          <span>Copy order</span>
        </button>
        {#if salesRepAddresses.length}
          <a
            href={buildMailtoLink(order, retailer, salesRepAddresses)}
            on:click={() => updateOrderAsSent()}
            on:click={() =>
              trackClick(null, [
                new OrderEntity({ id: order.id }),
                new DistributorEntity({ id: order.distributor.id }),
                new ManualOrderActionEntity({
                  action_type: "clicks_email_rep",
                  action_result: false,
                }),
              ])}
            class:fizz-button={$LayoutStore.isMobile}
            class:fizz-button-secondary={$LayoutStore.isDesktop}
            class="fizz-button-small fizz-button-icon button-margin"
          >
            <Icon type="MessageArrow" fill={$layoutStore.isMobile ? "#1B1B1B" : "white"} />
            <span>Send as email</span>
          </a>
        {/if}
      </div>
    </div>
    <div class="fizz-card fizz-card-padded">
      <div class="fizz-flex-row-responsive fizz-space-between fizz-align-content-start">
        <div>
          {#if !!order.po_number}
            <p class="fizz-text-small fizz-stack-4">
              <span class="fizz-text-strong">PO #</span>
              {order.po_number}
            </p>
            <p class="fizz-text-small fizz-stack-16">
              <span class="fizz-text-strong">Rep note:</span> For tracking, please include PO # on the
              delivery invoice
            </p>
          {/if}
          <p class="fizz-text-small fizz-text-strong">Order summary</p>
          <ul class="fizz-unordered-list fizz-stack-12">
            {#each order.products as product}
              <ActionRequiredDetailProduct {product} />
            {/each}
          </ul>
          {#if order.retailer_notes}
            <div class="fizz-stack-12">
              <p class="fizz-text-small fizz-text-strong">Order note</p>
              <p>{order.retailer_notes}</p>
            </div>
          {/if}
          {#if order.backup_notes}
            <div class="fizz-stack-12">
              <p class="fizz-text-small fizz-text-strong">Backup instructions</p>
              <p>{order.backup_notes}</p>
            </div>
          {/if}
        </div>
      </div>
    </div>
  </div>
</div>
